import type {IAssetsMap, IControlConfig} from "@buildwithflux/core";
import type {EnterpriseHandle, EnterpriseUid, IDocumentData, OrganizationUid} from "@buildwithflux/models";

import type {DocumentSelector, DocumentStoreState} from "../types";

/**
 * Base selectors for document metadata - direct state reads
 * These selectors perform simple property access without complex computation
 */

/** Gets the full document data (prefer granular selectors over this) */
export const data: DocumentSelector<IDocumentData | null> = (state: DocumentStoreState) => state.document;

/** Gets the document UID */
export const uid: DocumentSelector<string | undefined> = (state: DocumentStoreState) => state.uid;

/** Gets the document name */
export const name: DocumentSelector<string | undefined> = (state: DocumentStoreState) => state.name;

/** Gets the document description */
export const description: DocumentSelector<string | undefined> = (state: DocumentStoreState) => state.description;

/** Gets the document slug */
export const slug: DocumentSelector<string | undefined> = (state: DocumentStoreState) => state.slug;

/** Gets the document owner handle */
export const ownerHandle: DocumentSelector<string | undefined> = (state: DocumentStoreState) => state.owner_handle;

/** Gets the document owner UID */
export const ownerUid: DocumentSelector<string | undefined> = (state: DocumentStoreState) => state.owner_uid;

/** Gets the document preview image */
export const previewImage: DocumentSelector<string | undefined> = (state: DocumentStoreState) => state.preview_image;

/** Gets the document fork count */
export const forkCount: DocumentSelector<number | undefined> = (state: DocumentStoreState) => state.fork_count;

/** Gets the document star count */
export const starCount: DocumentSelector<number | undefined> = (state: DocumentStoreState) => state.star_count;

/** Gets the document updated_at timestamp */
export const updatedAt: DocumentSelector<number | undefined> = (state: DocumentStoreState) => state.updated_at;

/** Gets the document created_at timestamp */
export const createdAt: DocumentSelector<number | undefined> = (state: DocumentStoreState) => state.created_at;

/** Gets the document updated_by user UID */
export const updatedBy: DocumentSelector<string | undefined> = (state: DocumentStoreState) => state.updated_by;

/** Gets the fork_of_document_uid */
export const forkOfDocumentUid: DocumentSelector<string | undefined> = (state: DocumentStoreState) =>
    state.fork_of_document_uid;

/** Gets the belongs_to_part_uid */
export const belongsToPartUid: DocumentSelector<string | undefined> = (state: DocumentStoreState) =>
    state.belongs_to_part_uid;

/** Gets the localToDocumentUid (for local components) */
export const localToDocumentUid: DocumentSelector<string | undefined> = (state: DocumentStoreState) =>
    state.localToDocumentUid;

/** Gets the organization owner UID */
export const organizationOwnerUid: DocumentSelector<OrganizationUid | undefined> = (state: DocumentStoreState) =>
    state.organization_owner_uid;

/** Gets the enterprise owner UID */
export const enterpriseOwnerUid: DocumentSelector<EnterpriseUid | undefined> = (state: DocumentStoreState) =>
    state.enterprise_owner_uid;

/** Gets the enterprise owner handle */
export const enterpriseOwnerHandle: DocumentSelector<EnterpriseHandle | undefined> = (state: DocumentStoreState) =>
    state.enterprise_owner_handle;

/** Gets the organization_owner_is_enterprise_default flag */
export const organizationOwnerIsEnterpriseDefault: DocumentSelector<boolean | undefined> = (
    state: DocumentStoreState,
) => state.organization_owner_is_enterprise_default;

/** Gets the document contributor UIDs */
export const contributorUids: DocumentSelector<string[] | undefined> = (state: DocumentStoreState) =>
    state.contributor_uids;

/** Gets the document roles */
export const roles: DocumentSelector<IDocumentData["roles"] | undefined> = (state: DocumentStoreState) => state.roles;

/** Gets the document properties */
export const properties: DocumentSelector<IDocumentData["properties"] | undefined> = (state: DocumentStoreState) =>
    state.properties;

/** Gets the document assets */
export const assets: DocumentSelector<IAssetsMap> = (state: DocumentStoreState) => state.assets ?? {};

/** Gets the document controls */
export const controls: DocumentSelector<IControlConfig[] | undefined> = (state: DocumentStoreState) => state.controls;

/** Gets the document behavior (code) */
export const behaviour: DocumentSelector<string | undefined> = (state: DocumentStoreState) => state.behaviour;

/** Gets the document configs */
export const configs: DocumentSelector<IDocumentData["configs"] | undefined> = (state: DocumentStoreState) =>
    state.configs;

/** Gets the PCB layout rule sets */
export const pcbLayoutRuleSets: DocumentSelector<IDocumentData["pcbLayoutRuleSets"] | undefined> = (
    state: DocumentStoreState,
) => state.pcbLayoutRuleSets;

/** Gets the PCB layout nodes */
export const pcbLayoutNodes: DocumentSelector<IDocumentData["pcbLayoutNodes"] | undefined> = (
    state: DocumentStoreState,
) => state.pcbLayoutNodes;

/** Gets the routes */
export const routes: DocumentSelector<IDocumentData["routes"] | undefined> = (state: DocumentStoreState) =>
    state.routes;

/** Gets the route vertices */
export const routeVertices: DocumentSelector<IDocumentData["routeVertices"] | undefined> = (
    state: DocumentStoreState,
) => state.routeVertices;

/** Gets the comment threads */
export const commentThreads: DocumentSelector<IDocumentData["comment_threads"] | undefined> = (
    state: DocumentStoreState,
) => state.comment_threads;

/** Gets the organization member permission type */
export const organizationMemberPermissionType: DocumentSelector<
    IDocumentData["organization_member_permission_type"] | undefined
> = (state: DocumentStoreState) => state.organization_member_permission_type;

/** Gets the enterprise member permission type */
export const enterpriseMemberPermissionType: DocumentSelector<
    IDocumentData["enterprise_member_permission_type"] | undefined
> = (state: DocumentStoreState) => state.enterprise_member_permission_type;

/** Gets the receiveLatestDraftsForPartUids */
export const receiveLatestDraftsForPartUids: DocumentSelector<
    IDocumentData["receiveLatestDraftsForPartUids"] | undefined
> = (state: DocumentStoreState) => state.receiveLatestDraftsForPartUids;

/** Gets the document invites */
export const invites: DocumentSelector<IDocumentData["invites"] | undefined> = (state: DocumentStoreState) =>
    state.invites;

/** Gets whether this is a learning document */
export const isLearningDocument: DocumentSelector<boolean | undefined> = (state: DocumentStoreState) =>
    state.isLearningDocument;
